import type { DexieBlobStore } from '../../offline/infrastructure/dexie-blob-store.js';

/**
 * LocalIncidentPhotoSink (third adversarial review finding H3) — durably
 * stores an incident (pre-existing damage) photo in the Dexie `blobs` table,
 * mirroring `LocalPhotoSink`'s contract exactly (compress → durable store →
 * resolve; a compression/write failure is NEVER swallowed).
 *
 * Deliberately its OWN small class, not a reuse of `LocalPhotoSink`: that
 * class's `PhotoSink` port is typed to the fixed 4-value `PhotoAngle` enum,
 * matched 1:1 against `requestPhotoUploadUrls()`'s upload targets — incident
 * photos are a variable-length, angle-less list matched against
 * `requestIncidentPhotoUploadUrls(count)`'s own flat target list instead, so
 * forcing them through the same port/type would be a lie, not a
 * simplification.
 *
 * `slotId` is the caller's own STABLE per-photo identity (IncidentPhotos'
 * component-local `id`, e.g. "incident-1") — a retake of that SAME slot
 * reuses the SAME deterministic blob id `${outboxId}:incident:${slotId}`,
 * so Dexie's `put()` (primary-key upsert) overwrites the previous capture in
 * place instead of leaving an orphan behind (same fix as the second
 * adversarial review's BLOCKER #1 for the 4 mandatory angles).
 */
export class LocalIncidentPhotoSink {
  constructor(
    private readonly blobStore: DexieBlobStore,
    private readonly compress: (blob: Blob) => Promise<Blob>,
    private readonly getOutboxId: () => string,
  ) {}

  async upload(slotId: string, blob: Blob): Promise<{ key: string }> {
    const compressed = await this.compress(blob);
    const id = `${this.getOutboxId()}:incident:${slotId}`;

    await this.blobStore.put({
      id,
      outboxId: this.getOutboxId(),
      role: 'incident',
      blob: compressed,
      contentType: 'image/jpeg',
      bytes: compressed.size,
    });

    return { key: id };
  }

  /**
   * Fourth adversarial review, LOW #4 — deletes the blob for `slotId` (the
   * SAME deterministic id `upload()` would derive) right away when the
   * operator removes that incident slot, instead of leaving it for
   * `cleanupOrphanedBlobs`'s 24h-grace sweep.
   */
  async delete(slotId: string): Promise<void> {
    const id = `${this.getOutboxId()}:incident:${slotId}`;
    await this.blobStore.delete(id);
  }
}
